'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PrimeFactorizationCalculator() {
  const t = useTranslations("tools.primeFactorizationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [number, setNumber] = useState('');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const isPrime = (n: number): boolean => {
    if (n < 2) return false;
    if (n === 2) return true;
    if (n % 2 === 0) return false;
    for (let i = 3; i * i <= n; i += 2) {
      if (n % i === 0) return false;
    }
    return true;
  };

  const getPrimeFactors = (n: number): number[] => {
    const factors: number[] = [];
    let num = n;

    while (num % 2 === 0) {
      factors.push(2);
      num = num / 2;
    }

    for (let i = 3; i * i <= num; i += 2) {
      while (num % i === 0) {
        factors.push(i);
        num = num / i;
      }
    }

    if (num > 2) {
      factors.push(num);
    }

    return factors;
  };

  const getExponentialForm = (factors: number[]): string => {
    const factorCount: { [key: number]: number } = {};
    factors.forEach(f => {
      factorCount[f] = (factorCount[f] || 0) + 1;
    });

    const parts: string[] = [];
    for (const factor in factorCount) {
      if (factorCount[factor] === 1) {
        parts.push(factor);
      } else {
        parts.push(`${factor}^${factorCount[factor]}`);
      }
    }

    return parts.join(' × ');
  };

  const generateFactorTree = (n: number): string => {
    if (isPrime(n)) {
      return `${n} (${t('primeNumber')})`;
    }

    const lines: string[] = [];

    function factorize(num: number, level: number) {
      const spaces = '  '.repeat(level);

      if (isPrime(num)) {
        lines.push(spaces + num);
        return;
      }

      let divisor = 2;
      while (divisor <= num) {
        if (num % divisor === 0) {
          lines.push(`${spaces}${num} = ${divisor} × ${num / divisor}`);
          factorize(divisor, level + 1);
          factorize(num / divisor, level + 1);
          break;
        }
        divisor = divisor === 2 ? 3 : divisor + 2;
      }
    }

    factorize(n, 0);
    return lines.join('\n');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const num = parseInt(number);

    if (isNaN(num) || num < 2 || num > 1000000000) {
      setError(t('errorInvalidNumber'));
      return;
    }

    if (isPrime(num)) {
      setResults({
        inputNumber: num,
        isPrime: true,
        factorTree: generateFactorTree(num)
      });
    } else {
      const factors = getPrimeFactors(num);
      setResults({
        inputNumber: num,
        isPrime: false,
        primeFactors: factors,
        primeList: factors.join(', '),
        exponentialForm: getExponentialForm(factors),
        productForm: factors.join(' × '),
        factorTree: generateFactorTree(num)
      });
    }
  };

  const handleClear = () => {
    setNumber('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    yellow: '#fef9c3',
    yellowBorder: '#fde047',
    purple: '#7c3aed',
    lightBlue: '#dbeafe',
    gray: '#64748b',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { maxWidth: '700px', padding: '2rem', borderRadius: '1.25rem' },
    { maxWidth: '900px', padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const formStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      border: '2px solid #e2e8f0'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#334155', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      fontSize: '1rem'
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1rem' },
    { padding: '0.875rem 1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const, marginTop: '1.5rem' },
    { flexDirection: 'row' as const, marginTop: '1.5rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.blueHover} 100%)`,
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.gray} 0%, #475569 100%)`,
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.yellow} 0%, #fef9c3 100%)`,
      borderRadius: '0.75rem',
      border: `2px solid ${colors.yellowBorder}`
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#854d0e', fontWeight: 700, marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      borderLeft: `4px solid ${colors.blue}`
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultSectionH4Style = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600, marginBottom: '0.75rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const resultLabelStyle = getResponsiveStyle(
    { color: colors.textSecondary, fontWeight: 500 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const primeBadgeStyle = {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${colors.purple} 0%, #6d28d9 100%)`,
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    marginLeft: '0.5rem'
  };

  const factorTreeStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      fontFamily: 'monospace',
      lineHeight: 1.8,
      color: '#334155',
      overflowX: 'auto' as const,
      whiteSpace: 'pre' as const
    },
    { padding: '1rem', fontSize: '0.875rem' },
    { padding: '1.5rem', fontSize: '0.95rem' },
    { padding: '1.5rem', fontSize: '0.95rem' }
  );

  const errorStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
      color: '#991b1b',
      borderRadius: '0.5rem',
      border: '2px solid #fca5a5',
      marginTop: '1rem',
      fontWeight: 500
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const sectionH2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '1.75rem' },
    { marginBottom: '2rem' }
  );

  const referenceStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.4)',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      borderLeft: `3px solid ${colors.primary}`
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem', padding: '1.25rem' },
    { fontSize: '1rem', padding: '1.5rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🔢 {t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="number-input">
                {t('inputLabel')}
              </label>
              <input
                style={inputStyle}
                type="number"
                id="number-input"
                placeholder={t('inputPlaceholder')}
                min="2"
                max="1000000000"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = colors.blue}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                required
              />
            </div>

            <div style={buttonGroupStyle}>
              <button
                style={calculateButtonStyle}
                type="submit"
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('calculateButton')}
              </button>
              <button
                style={clearButtonStyle}
                type="button"
                onClick={handleClear}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultSectionStyle}>
                <h4 style={resultSectionH4Style}>{t('numberInfoTitle')}</h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('inputNumber')}</span>
                  <span style={resultValueStyle}>{results.inputNumber.toLocaleString()}</span>
                </div>
                <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                  <span style={resultLabelStyle}>{t('numberType')}</span>
                  <span style={resultValueStyle}>
                    {results.isPrime ? t('primeNumberType') : t('compositeNumberType')}
                    {results.isPrime && <span style={primeBadgeStyle}>{t('primeBadge')}</span>}
                  </span>
                </div>
              </div>

              {!results.isPrime && (
                <div style={resultSectionStyle}>
                  <h4 style={resultSectionH4Style}>{t('primeFactorizationTitle')}</h4>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('primeFactorsList')}</span>
                    <span style={resultValueStyle}>{results.primeList}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('exponentialForm')}</span>
                    <span style={resultValueStyle}>{results.exponentialForm}</span>
                  </div>
                  <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                    <span style={resultLabelStyle}>{t('productForm')}</span>
                    <span style={resultValueStyle}>{results.productForm}</span>
                  </div>
                </div>
              )}

              <div style={{ ...resultSectionStyle, marginBottom: 0 }}>
                <h4 style={resultSectionH4Style}>{t('factorTreeTitle')}</h4>
                <div style={factorTreeStyle}>
                  {results.factorTree}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={sectionH2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={sectionH2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('step1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step4')}</li>
          <li>{t('step5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={sectionH2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('application1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('application2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('application3')}</li>
          <li>{t('application4')}</li>
        </ul>

        <h3 style={h3Style}>{t('methodsTitle')}</h3>
        <p style={paragraphStyle}>{t('methodsParagraph')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('method1')}</li>
          <li>{t('method2')}</li>
        </ul>

        <h3 style={h3Style}>{t('fundamentalTheoremTitle')}</h3>
        <p style={paragraphStyle}>{t('fundamentalTheoremParagraph')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={sectionH2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('whatIsPrimeTitle')}</h3>
        <p style={paragraphStyle}>{t('whatIsPrimeParagraph')}</p>

        <h3 style={h3Style}>{t('divisionMethodTitle')}</h3>
        <p style={paragraphStyle}>{t('divisionMethodParagraph')}</p>

        <h3 style={h3Style}>{t('factorTreeMethodTitle')}</h3>
        <p style={paragraphStyle}>{t('factorTreeMethodParagraph')}</p>

        <h3 style={h3Style}>{t('examplesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('example1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('example2')}</li>
          <li>{t('example3')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={sectionH2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={{ marginBottom: 0 }}>
          <h3 style={h3Style}>{t('faq5Question')}</h3>
          <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={{ ...glassCardStyle, marginBottom: 0 }}>
        <h2 style={sectionH2Style}>{t('referencesTitle')}</h2>

        <div style={referenceStyle}>
          <a
            href="https://www.geeksforgeeks.org/utilities/prime-factorization-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference1')}
          </a>
        </div>

        <div style={referenceStyle}>
          <a
            href="https://www.calculatorsoup.com/calculators/math/prime-factors.php"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference2')}
          </a>
        </div>

        <div style={referenceStyle}>
          <a
            href="https://byjus.com/maths/prime-factorization/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference3')}
          </a>
        </div>

        <div style={referenceStyle}>
          <a
            href="https://www.cuemath.com/numbers/prime-factorization/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference4')}
          </a>
        </div>

        <div style={{ ...referenceStyle, marginBottom: 0 }}>
          <a
            href="https://www.mathsisfun.com/prime-factorization.html"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference5')}
          </a>
        </div>
      </section>
    </div>
  );
}